import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { SHARED_MODULES } from 'src/app/modules/shared-imports';
import { ScreenSupportService, SupportSession } from '../screen-support.service';
import { ScreenSupportRoomComponent } from '../screen-support-room/screen-support-room.component';

/**
 * Sits once in the app shell. When support asks the logged-in person to show their screen (or answers their request), a
 * window opens on whatever page they are on: "Support would like to see your screen". It only LISTENS to ScreenSupportService.myStatus$.
 * The first look and the live watch are started by NotificationSocketService.init(), which the shell calls only after login (opening a
 * live connection before login just spins on failed 401 handshakes), and again when the server says something changed (the
 * SCREEN_SUPPORT live event and the SCREEN_SUPPORT_REQUESTED notification both end up in ScreenSupportService.status()). "Not now" only closes the window:
 * the invitation stays in the ticket until it expires (30 minutes).
 */
@Component({
  selector: 'app-screen-support-prompt',
  standalone: true,
  imports: [CommonModule, SHARED_MODULES, ScreenSupportRoomComponent],
  templateUrl: './screen-support-prompt.component.html',
})
export class ScreenSupportPromptComponent implements OnInit, OnDestroy {

  session: SupportSession | null = null;
  showPrompt = false;
  roomOpen = false;

  /** Invitations the person already closed with "Not now" in this tab, so the window does not come back for the same one. */
  private dismissed = new Set<number>();
  private subs: Subscription[] = [];

  constructor(private api: ScreenSupportService, private router: Router) {}

  ngOnInit(): void {
    this.subs.push(this.api.myStatus$.subscribe((st) => this.consider(st?.session ?? null)));
  }

  ngOnDestroy(): void {
    this.subs.forEach((s) => s.unsubscribe());
  }

  private consider(s: SupportSession | null): void {
    this.session = s;
    const waiting = !!s && s.status === 'INVITED' && s.canJoin && !this.dismissed.has(s.id);
    if (waiting && !this.roomOpen) this.showPrompt = true;
    if (!s || s.status !== 'INVITED') this.showPrompt = false;
  }

  openNow(): void {
    this.showPrompt = false;
    this.roomOpen = true;
  }

  notNow(): void {
    if (this.session) this.dismissed.add(this.session.id);
    this.showPrompt = false;
  }

  openTicket(): void {
    const id = this.session?.ticketId;
    if (this.session) this.dismissed.add(this.session.id);
    this.showPrompt = false;
    if (id != null) this.router.navigate(['/support/tickets', id]);
  }

  /** The room window closed or the session changed: look again. */
  refresh(): void {
    this.api.status().subscribe({ error: () => {} });
  }
}
